import React from "react";

import { AlertTriangle, RefreshCw } from "lucide-react";

// Key used to prevent infinite reload loops on chunk failures
const CHUNK_RELOAD_KEY = "cmp-chunk-reload";

/**
 * Detects stale chunk / dynamic import errors.
 */
function isChunkLoadError(error) {
  if (!error) return false;
  const msg = error.toString();
  return (
    msg.includes("dynamically imported") ||
    msg.includes("Failed to fetch dynamically") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk")
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Uncaught Error:", error, errorInfo);
    this.setState({ errorInfo });

    // Auto-reload on stale chunk errors (once per session to avoid loops)
    if (isChunkLoadError(error)) {
      const lastReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      const now = Date.now();
      // Only auto-reload if we haven't reloaded in the last 10 seconds
      if (!lastReload || now - Number(lastReload) > 10000) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
        window.location.reload();
        return;
      }
    }
  }

  handleRetry() {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      const isChunkError = isChunkLoadError(this.state.error);

      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6">
          <div className="max-w-md w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-8 text-center animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6 text-red-500">
              <AlertTriangle size={32} />
            </div>

            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              {isChunkError ? "Update Available" : "Something went wrong"}
            </h2>

            <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm">
              {isChunkError
                ? "A new version of the app has been deployed. Click below to load the latest version."
                : "We encountered an unexpected error. The application has been paused to prevent further issues."}
            </p>

            {!isChunkError && this.state.error && (
              <div className="text-left bg-slate-100 dark:bg-slate-950 p-4 rounded-lg mb-6 overflow-auto max-h-40 border border-slate-200 dark:border-slate-800">
                <code className="text-xs text-red-500 font-mono break-all">
                  {this.state.error.toString()}
                </code>
              </div>
            )}

            <button
              onClick={this.handleRetry}
              className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw size={18} />{" "}
              {isChunkError ? "Update Now" : "Reload Application"}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
